import { useContext } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { buildFullLink, getPath } from "common/routing";
import { getPublicPortalPrefix } from "common/utils/url";
import { usePublicUserInviteQuery } from "../../../generated/graphql";
import { colors, spacing } from "../../../stitches.config";
import { Container as LoginContainer } from "../../Authentication/CommonLoginComponents/__styles__/FormWithFullPageBackground";
import { AuthContext } from "../../Authorization/AuthContext";
import { CenterContent } from "../../Common/__styles__/Layout";
import { Button } from "../../Common/Button";
import { Section, Sections } from "../../Common/Layout";
import { Body, Title } from "../../Common/Typography";
import { GuestLocationState } from "../../Guest";
import Layout from "../Layout";
import { INVITE_FORM_MAX_WIDTH } from "./constants";
import { PublicUserInvite } from "./PublicUserInvite";

const PublicUserInvitePage = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const location = useLocation();
  const history = useHistory();
  const { publicUser } = useContext(AuthContext);

  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get("redirect");

  const { data, loading, error } = usePublicUserInviteQuery({
    variables: { inviteId },
  });

  if (error || loading) return null;

  if (!data?.publicUserInvite) {
    return (
      <Layout
        backgroundColor={colors.bgUiContainerInverted}
        footerFontColor="contentPrimaryInverted"
      >
        <CenterContent style={{ padding: spacing.l.value, flex: 1 }}>
          <LoginContainer style={{ maxWidth: INVITE_FORM_MAX_WIDTH }}>
            <Sections>
              <Section>
                <Title type="semiBold" size="large">
                  Invite not found
                </Title>
                <Body size="default" type="regular">
                  This invite link is invalid or has expired.
                </Body>
              </Section>
              <Button
                styleVariant="primary"
                size="medium"
                stretch
                onClick={() => history.push(getPath("guestHome"))}
              >
                Go to homepage
              </Button>
            </Sections>
          </LoginContainer>
        </CenterContent>
      </Layout>
    );
  }

  const invite = data.publicUserInvite;

  if (invite.acceptedAt) {
    if (publicUser) {
      window.location.href = redirect || "/";
      return null;
    }

    const homePath = redirect
      ? `${getPath("guestHome")}?redirect=${encodeURIComponent(redirect)}`
      : getPath("guestHome");

    history.push(homePath, {
      openLoginModal: true,
    } satisfies GuestLocationState);
    return null;
  }

  const currentUrl = window.location.href;
  const { hostname } = new URL(currentUrl);

  if (!hostname.startsWith(`${invite.account.subdomain}.`)) {
    const prefix = getPublicPortalPrefix(invite.account.subdomain);
    window.location.href = buildFullLink("acceptPublicUserInvite", {
      params: { inviteId },
      prefix,
    });
    return null;
  }

  return <PublicUserInvite invite={invite} redirect={redirect} />;
};

export default PublicUserInvitePage;
